import React from 'react'
import PropTypes from 'prop-types'
import { Grid, CircularProgress, Typography, IconButton, Tooltip, useTheme, useMediaQuery, Paper, Box, Collapse, Divider } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

import { CustomTable, ModalConfirmacao } from 'components'
import { DetailPanel } from 'components/DescricoesVestigiosExame/DescricoesTecnicasVestigio/DetailPanel'
import { getLabelTipoVestigioEspecifico } from 'helpers/labels'
import { useDescricoesComAnalises } from './useDescricoesComAnalises'
import { useStyles } from './DescricoesComAnalises.styles'
import { FormAnaliseConstatacao } from './components/FormAnaliseConstatacao'

export const DescricoesComAnalises = (props) => {
  const { idVestigio, idExameRealizado, definitivo } = props
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const {
    descricoesTecnicas,
    loading,
    processandoOperacao,
    tiposSubstanciasQuimicas,
    tiposMetodologias,
    openFormularioAnaliseConstatacao,
    setOpenFormularioAnaliseConstatacao,
    isFormularioInvalido,
    formAnaliseConstatacao,
    handleClickOpenFormularioAnaliseConstatacao,
    handleConfirmarCadastroAnaliseConstatacao,
    expandedCards,
    toggleCardExpansion,
  } = useDescricoesComAnalises({ idVestigio, idExameRealizado, definitivo })

  if (loading && descricoesTecnicas.length === 0) {
    return (
      <div className={classes.carregando}>
        <CircularProgress disableShrink />
      </div>
    )
  }

  const columns = [
    {
      title: 'NATUREZA (GENÉRICA)',
      field: 'tipoVestigioGenerico',
      headerStyle: { fontWeight: 'bold' },
    },
    {
      title: 'NATUREZA (ESPECÍFICA)',
      render: (rowData) => getLabelTipoVestigioEspecifico(rowData.tipoVestigioEspecifico)?.toUpperCase() || '-',
      headerStyle: { fontWeight: 'bold' },
    },
    {
      title: 'UNIDADES',
      field: 'unidades',
      headerStyle: { fontWeight: 'bold' },
    },
    {
      title: 'RESULTADO',
      render: (rowData) => (rowData.analiseConstatacao ? <span className={classes.realizada}>REALIZADA</span> : <span className={classes.naoRealizada}>NÃO REALIZADA</span>),
      headerStyle: { fontWeight: 'bold' },
    },
  ]

  return (
    <div className={classes.root}>
      {isMobile ? (
        descricoesTecnicas.length > 0 ? (
          <Grid container spacing={1}>
            {descricoesTecnicas.map((dt) => {
              const isExpanded = !!expandedCards[dt.id]
              return (
                <Grid item xs={12} key={dt.id}>
                  <Paper className={classes.cardMobile}>
                    <div className={classes.cardMobileHeader}>
                      <Typography className={classes.cardMobileTitle}>{dt.tipoVestigioGenerico}</Typography>
                      <div className={classes.cardMobileActions}>
                        <Tooltip title="Realizar análise de constatação">
                          <IconButton size="small" onClick={() => handleClickOpenFormularioAnaliseConstatacao(dt)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => toggleCardExpansion(dt.id)}>
                          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </div>
                    </div>

                    <Typography className={classes.cardMobileLabel}>Natureza específica</Typography>
                    <Typography className={classes.cardMobileValue}>{getLabelTipoVestigioEspecifico(dt.tipoVestigioEspecifico)?.toUpperCase() || '-'}</Typography>

                    <Typography className={classes.cardMobileLabel}>Unidades</Typography>
                    <Typography className={classes.cardMobileValue}>{dt.unidades || '-'}</Typography>

                    <Typography className={classes.cardMobileLabel}>Resultado</Typography>
                    <Typography className={dt.analiseConstatacao ? classes.realizada : classes.naoRealizada}>{dt.analiseConstatacao ? 'REALIZADA' : 'NÃO REALIZADA'}</Typography>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <div className={classes.cardMobileDetail}>
                        <DetailPanel idDescricaoTecnica={dt.id} tipoVestigio={dt.tipoVestigioGenerico}>
                          <Box mt={1} mb={1}>
                            <Typography className={classes.detailTitle}>ANÁLISE DE CONSTATAÇÃO</Typography>
                          </Box>
                          {dt.analiseConstatacao ? (
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <Typography className={classes.dadoEspelho}>Resultado</Typography>
                                <Typography className={classes.valorEspelho}>{definitivo ? (dt.analiseConstatacao.resultado?.toUpperCase() === 'INDICATIVO' ? 'POSITIVO' : 'NEGATIVO') : dt.analiseConstatacao.resultado?.toUpperCase()}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography className={classes.dadoEspelho}>Testes Empregados</Typography>
                                <Typography className={classes.valorEspelho}>{dt.analiseConstatacao.metodConsts?.map((m) => m.nome?.toUpperCase()).join(', ') || '-'}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography className={classes.dadoEspelho}>Substâncias</Typography>
                                <Typography className={classes.valorEspelho}>{dt.analiseConstatacao.subsQuims?.map((q) => q.nome?.toUpperCase()).join(', ') || '-'}</Typography>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography className={classes.naoRealizada}>Não foi realizada análise de constatação para esse item neste exame.</Typography>
                          )}
                        </DetailPanel>
                      </div>
                    </Collapse>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>
        ) : (
          <Typography align="center" className={classes.naoRealizada} style={{ padding: '20px' }}>
            Nenhuma descrição técnica associada a este vestígio
          </Typography>
        )
      ) : (
        <CustomTable
          title=""
          columns={columns}
          data={descricoesTecnicas}
          emptyDataSourceMessage="Nenhuma descrição técnica associada a este vestígio"
          onRowClick={(event, rowData, togglePanel) => togglePanel()}
          options={{
            search: false,
            paging: false,
            toolbar: false,
            headerStyle: {
              backgroundColor: theme.palette.background.default,
              fontSize: '13px',
            },
          }}
          actions={[
            {
              icon: () => <EditIcon fontSize="small" />,
              tooltip: 'Realizar análise de constatação',
              onClick: (event, rowData) => handleClickOpenFormularioAnaliseConstatacao(rowData),
            },
          ]}
          detailPanel={(rowData) => (
            <div className={classes.detailPanel}>
              <DetailPanel idDescricaoTecnica={rowData.id} tipoVestigio={rowData.tipoVestigioGenerico}>
                <Grid item>
                  <Typography className={classes.detailTitle}>ANÁLISE DE CONSTATAÇÃO</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.dividerTitle} />
                </Grid>
                {rowData.analiseConstatacao ? (
                  <Grid container spacing={2} className={classes.grupoEspelho}>
                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.dadoEspelho}>Resultado</Typography>
                      <Typography className={classes.valorEspelho}>{definitivo ? (rowData.analiseConstatacao.resultado?.toUpperCase() === 'INDICATIVO' ? 'POSITIVO' : 'NEGATIVO') : rowData.analiseConstatacao.resultado?.toUpperCase()}</Typography>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.dadoEspelho}>Testes Empregados</Typography>
                      <Typography className={classes.valorEspelho}>{rowData.analiseConstatacao.metodConsts?.map((m) => m.nome?.toUpperCase()).join(', ') || '-'}</Typography>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.dadoEspelho}>Substâncias</Typography>
                      <Typography className={classes.valorEspelho}>{rowData.analiseConstatacao.subsQuims?.map((q) => q.nome?.toUpperCase()).join(', ') || '-'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography className={classes.naoRealizada}>Não foi realizada análise de constatação para esse item neste exame.</Typography>
                )}
              </DetailPanel>
            </div>
          )}
        />
      )}

      <ModalConfirmacao
        aberto={openFormularioAnaliseConstatacao}
        onClose={() => setOpenFormularioAnaliseConstatacao(false)}
        onConfirm={handleConfirmarCadastroAnaliseConstatacao}
        disabled={isFormularioInvalido}
        processando={processandoOperacao}
        titulo="ANÁLISE DE CONSTATAÇÃO"
        confirmarLabel="Confirmar"
        cancelarLabel="Cancelar"
        confirmarColor="success"
        maxWidth="sm"
      >
        <FormAnaliseConstatacao formAnaliseConstatacao={formAnaliseConstatacao} tiposSubstanciasQuimicas={tiposSubstanciasQuimicas} tiposMetodologias={tiposMetodologias} definitivo={definitivo} />
      </ModalConfirmacao>
    </div>
  )
}

DescricoesComAnalises.propTypes = {
  idVestigio: PropTypes.number.isRequired,
  idExameRealizado: PropTypes.number.isRequired,
  definitivo: PropTypes.bool,
}
